import Container from 'components/container/container';
import ProductPage from 'components/product';
import {
  ProductDivider,
  ProductFeatureContainer,
  ProductFeatureLayoutGrid,
  ProductFeatureLayoutGridNode,
  ProductFeatureRequest,
  ProductFeatureShowMoreContainer,
  ProductFeatureShowMoreLink,
} from 'components/product/product.styles';
import { ProductFeatureStatus } from 'components/product/product.types';
import Link from 'next/link';
import { useState } from 'react';

import { LIBUM_FEEDBACK, LIBUM_URL, POWERON_PIPELINES_MARKETPLACE_URL } from 'shared/constants/globals';
import { PRODUCTS } from 'shared/data/products';

const POWERON_PIPELINES = PRODUCTS.find((p) => p.id === 'poweron-pipelines')!;

export default function PowerOnPipelines() {
  const title = 'PowerOn Pipelines | Libum';
  const description =
    'PowerOn Pipelines brings true CI/CD capabilities to Symitar PowerOn development with automated validation and deployment for Azure DevOps and GitHub Actions.';
  const keywords =
    'PowerOn Pipelines,PowerOn CI/CD,PowerOn Azure DevOps,PowerOn GitHub Actions,PowerOn deployment,PowerOn validation,Symitar automation,PowerOn,Symitar,Symitar software,Jack Henry software,credit union software';
  const url = `${LIBUM_URL}/poweron-pipelines`;

  const [showMore, setShowMore] = useState(false);
  const handleShowMoreClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setShowMore(!showMore);
  };

  const getStatusDisplayName = (status: ProductFeatureStatus): string => {
    return status
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  return (
    <Container title={title} description={description} keywords={keywords} url={url} socialCard="poweron-pipelines">
      <div style={{ width: '100%' }}>
        <ProductPage product={POWERON_PIPELINES}>
          <h1>PowerOn Pipelines</h1>
          <h2>Deploy, install, and validate your PowerOns within your pipelines.</h2>
          <ProductDivider />
          <p id="overview">
            In early 2025, we had a forward-thinking credit union reach out and ask for us to fill the version control
            gap for the Symitar core — a few months later, we published{' '}
            <Link href={POWERON_PIPELINES_MARKETPLACE_URL}>PowerOn Pipelines</Link> for Azure DevOps.
          </p>
          <p>
            With PowerOn Pipelines, your organization can maintain a central repository for your PowerOn files using
            your preferred CI/CD platform to validate changes during pull requests, orchestrate multi-stage deployments,
            and prevent invalid code from progressing — all while maintaining complete audit trails and role-based
            approvals.
          </p>
          <ProductDivider />
          <ProductFeatureContainer id="features">
            <ProductFeatureLayoutGrid className="featured">
              {POWERON_PIPELINES.features
                .filter((f) => f.isFeatured)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={`isFeatured ${feature.docsLink ? 'has-link' : ''}`}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="image">
                      {/* eslint-disable-next-line */}
                      <img src={feature.imagePath} style={{ maxHeight: '100%' }} />
                    </div>
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid>
              {POWERON_PIPELINES.features
                .filter((f) => !f.isFeatured && !f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid className={showMore ? 'opened' : 'hidden'}>
              {POWERON_PIPELINES.features
                .filter((f) => f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className="details">
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
          </ProductFeatureContainer>
          <ProductFeatureShowMoreContainer>
            <Link href="" passHref legacyBehavior>
              <ProductFeatureShowMoreLink onClick={(e: any) => handleShowMoreClick(e)}>
                {showMore ? 'Show less' : 'Show more'}
              </ProductFeatureShowMoreLink>
            </Link>
          </ProductFeatureShowMoreContainer>
          <ProductDivider />
          <ProductFeatureRequest>
            <h3>Have a feature you&apos;d like to see in PowerOn Pipelines?</h3>
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Submit Your Idea
            </Link>
          </ProductFeatureRequest>
        </ProductPage>
      </div>
    </Container>
  );
}
